import Browser from "./utils/browser.js";
import Mediapipe from "./utils/mediapipe.js";
import Snapshot from "./utils/snapshot.js";
import Livekit from "./utils/livekit.js";
import Hardware from "./utils/hardware.js";
import { apiClient } from "./utils/apiClient.js";
import UI from "./components/ui.js";
import Chat from "./utils/chat.js";

class ProctoringSDK {
  static videoElement = null;
  static cameraStream = null;
  static screenStream = null;
  static ui = UI;
  static initialized = false;
  static attempt_id = null; // Renamed to snake_case
  static durationConsumedSeconds = 0;
  static durationIntervalId = null;
  static durationDisplayIntervalId = null;
  static durationSyncInFlight = false;
  static DURATION_SYNC_SECONDS = 15;

  // Modes configuration
  static modes = {
    mode_full: {
      live_streaming: true,
      snapshots: false,
      ai_analysis: true,
      browser_locking: true,
    },

    mode_live_ai: {
      live_streaming: true,
      snapshots: false,
      ai_analysis: true,
      browser_locking: true,
    },

    mode_record_ai: {
      live_streaming: true,
      snapshots: false,
      ai_analysis: true,
      browser_locking: true,
    },

    mode_snapshot_ai: {
      live_streaming: false,
      snapshots: true,
      ai_analysis: true,
      browser_locking: true,
    },

    mode_strict_full: {
      live_streaming: true,
      snapshots: true,
      ai_analysis: true,
      browser_locking: true,
    },
  };

  // Default configuration
  static config = {
    attempt: {
      id: null,
      user_id: null,
      session_id: null,
      institution_id: null,
      student_name: "Student",
      student_email: "student@example.com",
      exam_name: "Proctored Exam",
      mode: "mode_full",
      session_duration: 60,
      allowed_chances: 3,
    },
    ui: {
      enabled: true,
      theme: "dark",
      auto_fullscreen: true,
    },
    livekit: {
      enabled: true,
    },
    snapshot: {
      image_quality: 0.8, // Renamed
      image_format: "image/jpeg", // Renamed
      periodic_interval: 120000, // Default 2 minutes
    },
    callbacks: {
      // on_violation: null,
      on_connect: null,
      // on_disconnect: null,
      on_error: null,
      // on_chat_message: null,
      onStop: null,
      on_duration_update: null,
    },
    hardware: {
      recovery_enabled: true,
      recovery_delay_ms: 1000,
      health_check_interval_ms: 5000,
      callbacks: {
        on_device_disconnected: null,
        on_device_reconnected: null,
      },
    },
  };

  static init(options = {}) {
    // Merge attempt config
    if (options.attempt) {
      this.config.attempt = {
        ...this.config.attempt,
        ...options.attempt,
      };
    }

    // Merge UI config
    if (options.ui !== undefined) {
      if (typeof options.ui === "boolean") {
        this.config.ui.enabled = options.ui;
      } else {
        this.config.ui = { ...this.config.ui, ...options.ui };
      }
    }

    // Merge LiveKit config
    if (options.livekit !== undefined) {
      if (typeof options.livekit === "boolean") {
        this.config.livekit.enabled = options.livekit;
      } else {
        this.config.livekit = {
          ...this.config.livekit,
          ...options.livekit,
        };
      }
    }

    // Merge Snapshot config
    if (options.snapshot) {
      this.config.snapshot = {
        ...this.config.snapshot,
        ...options.snapshot,
      };
    }

    // Merge callbacks
    if (options.callbacks) {
      this.config.callbacks = {
        ...this.config.callbacks,
        ...options.callbacks,
      };
    }

    // Merge hardware config
    if (options.hardware) {
      this.config.hardware = {
        ...this.config.hardware,
        ...options.hardware,
        callbacks: {
          ...this.config.hardware.callbacks,
          ...(options.hardware.callbacks || {}),
        },
      };
    }

    // Initialize UI if enabled
    if (this.config.ui.enabled) {
      UI.init({ theme: this.config.ui.theme });
    }

    // Auto fullscreen if enabled
    if (this.config.ui.auto_fullscreen) {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen().catch(() => {});
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen().catch(() => {});
      }
    }

    // Initialize Snapshot with config
    Snapshot.init(this.config.snapshot);

    this.initialized = true;
  }

  static async start() {
    try {
      if (!this.initialized) {
        this.init();
      }

      // Get current mode configuration
      const currentMode =
        this.modes[this.config.attempt.mode] || this.modes.mode_full;

      const streams = await this.startPermissionWizard();

      this.cameraStream = streams.camera;
      this.screenStream = streams.screen;

      if (!this.cameraStream) {
        throw new Error("Camera permission is required");
      }

      if (!this.videoElement) {
        this.videoElement = document.createElement("video");
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
        this.videoElement.muted = true;
        this.videoElement.style.display = "none";
        document.body.appendChild(this.videoElement);
      }

      this.videoElement.srcObject = this.cameraStream;

      UI.updateStatus(!!this.cameraStream, !!this.screenStream);
      UI.setCameraStream(this.cameraStream);
      UI.showCameraPreview();

      // Initialize hardware monitoring for error recovery
      Hardware.init({
        config: {
          recovery_enabled: this.config.hardware.recovery_enabled,
          recovery_delay_ms: this.config.hardware.recovery_delay_ms,
          health_check_interval_ms:
            this.config.hardware.health_check_interval_ms,
          show_recovery_dialog: true, // Show dialog for user to reshare
        },
        callbacks: {
          on_device_disconnected: (device_type, error) => {
            console.log(
              `[Proctoring] ${device_type} disconnected:`,
              error.message,
            );

            // Stop Mediapipe when camera disconnects to prevent false "no_face" violations
            if (device_type === "camera" || device_type === "mic") {
              Mediapipe.stop();
            }

            this.config.hardware.callbacks.on_device_disconnected?.(
              device_type,
              error,
            );
          },
          on_device_reconnected: async (device_type, stream) => {
            console.log(`[Proctoring] ${device_type} reconnected`);

            // Update internal stream references
            if (device_type === "camera" || device_type === "mic") {
              this.cameraStream = stream;
              this.videoElement.srcObject = stream;
              UI.setCameraStream(stream);

              // Update Snapshot with new video element reference
              Snapshot.init({ camera: this.videoElement });

              // Republish camera/mic tracks to LiveKit
              if (currentMode.live_streaming) {
                await Livekit.republishCamera(stream);
              }

              // Restart Mediapipe with new track
              if (currentMode.ai_analysis) {
                const videoTrack = stream.getVideoTracks()[0];
                await Mediapipe.start(videoTrack, this.videoElement);
              }
            } else if (device_type === "screen") {
              this.screenStream = stream;
              // Update Snapshot with new screen stream
              Snapshot.init({ screen: this.screenStream });

              // Republish screen track to LiveKit
              if (currentMode.live_streaming) {
                await Livekit.republishScreen(stream);
              }
            }

            UI.updateStatus(!!this.cameraStream, !!this.screenStream);
            UI.showNotification(
              `${device_type.charAt(0).toUpperCase() + device_type.slice(1)} Restored`,
              `Your ${device_type} has been successfully reconnected.`,
              "success",
            );
            this.config.hardware.callbacks.on_device_reconnected?.(
              device_type,
              stream,
            );
          },
          on_error: (error) => {
            console.error("[Proctoring] Hardware error:", error);
            this.config.callbacks.on_error?.(error);
          },
        },
      });
      Hardware.monitorStreams(this.cameraStream, this.screenStream);

      await new Promise((resolve) => {
        this.videoElement.onloadedmetadata = () => {
          this.videoElement.play();
          resolve();
        };
      });

      // Handler mapping
      const violation_callback = (type) => this.handleViolation(type);

      // Initialize browser monitoring if enabled (strict mode)
      if (currentMode.browser_locking) {
        Browser.init({ onViolationDetected: violation_callback });
      }

      // Initialize AI analysis if enabled
      if (currentMode.ai_analysis) {
        Mediapipe.init({ onViolationDetected: violation_callback });
        const videoTrack = this.cameraStream.getVideoTracks()[0];
        await Mediapipe.start(videoTrack, this.videoElement);
      }

      // Always initialize Snapshot with camera and screen sources
      // (needed for violation snapshots even if periodic snapshots are disabled)
      const periodic_interval = currentMode.snapshots
        ? this.config.snapshot.periodic_interval || 120000
        : 0;

      Snapshot.init({
        ...this.config.snapshot,
        camera: this.videoElement,
        screen: this.screenStream,
        periodic_interval: periodic_interval,
        onCapture: async (images, event) => {
          if (event === "periodic_snapshot") {
            try {
              const formData = new FormData();
              formData.append("attemptId", this.config.attempt.id);
              formData.append("eventType", event);
              formData.append("timestamp", new Date().toISOString());

              if (images.camera) {
                formData.append("camera", images.camera, "camera.jpg");
              }

              if (images.screen) {
                formData.append("screen", images.screen, "screen.jpg");
              }

              if (images.camera || images.screen) {
                await apiClient.post(
                  "/client/proctoring/image/upload",
                  formData,
                );
                console.log(`[Proctoring] Images uploaded for ${event}`);
              }
            } catch (error) {
              console.error(
                "[Proctoring] Failed to upload periodic images:",
                error,
              );
            }
          }
        },
      });

      this.attempt_id = this.config.attempt.id;
      this.startDurationMonitoring();

      // Handle live streaming and recording based on mode
      if (currentMode.live_streaming) {
        // Initialize Chat utility
        Chat.init({
          attemptId: this.config.attempt.id, // Pass attemptId
          sessionId: this.config.attempt.session_id,
          userId: this.config.attempt.user_id,
          role: "user",
          callbacks: {
            onMessage: (message) => {
              if (this.config.ui.enabled) {
                UI.handleChatMessage(message);
              }
              this.config.callbacks.on_chat_message?.(message);
            },
            onError: (err) => {
              console.error("[Proctoring] Chat error:", err);
            },
          },
        });

        // Load chat history after initialization
        if (this.config.ui.enabled) {
          UI.loadChatHistory();
        }

        Livekit.init({
          callbacks: {
            onConnected: () => {
              console.log("[Proctoring] LiveKit connected");
              this.config.callbacks.on_connect?.();
            },
            onDisconnected: () => {
              console.log("[Proctoring] LiveKit disconnected");
              this.config.callbacks.on_disconnect?.();
            },
            onError: (err) => {
              console.error("[Proctoring] LiveKit error:", err);
              this.config.callbacks.on_error?.(err);
            },
            onChatMessage: (data) => {
              // console.log(
              //     "[Proctoring] Received chat message via LiveKit:",
              //     data,
              // );
              if (this.config.ui.enabled) {
                UI.handleChatMessage(data);
              }
              this.config.callbacks.on_chat_message?.(data);
            },
            onStopExam: (data) => {
              console.log(
                "[Proctoring] Received stop command from server:",
                data,
              );
              if (this.config.ui.enabled) {
                UI.showFullscreenCountdown(
                  "Exam Session Ended",
                  data.reason ||
                    "The exam has been stopped by the proctor/system.",
                  5,
                  () => {
                    this.stop(true);
                    // Optional: Redirect or reload after stop?
                    // window.location.reload();
                  },
                );
              } else {
                // Fallback if UI is disabled
                this.stop(true);
              }
            },
          },
        });
        await Livekit.start(
          this.cameraStream,
          this.screenStream,
          this.token,
          this.server_url,
        );
      }

      return { success: 1 };
    } catch (error) {
      console.error("Failed to start Proctoring SDK:", error);
      this.config.callbacks.on_error?.(error);
      this.stop();
      return { success: 0, error: error.message };
    }
  }

  // Exposed Chat Methods
  static async sendChatMessage(message) {
    return await Chat.sendMessage(message);
  }

  static async getChatHistory(limit = 50, offset = 0) {
    return await Chat.getHistory(limit, offset);
  }

  static emitDurationUpdate() {
    if (this.config.ui.enabled) {
      UI.updateDuration(this.durationConsumedSeconds);
    }
    this.config.callbacks.on_duration_update?.(this.durationConsumedSeconds);
  }

  static startDurationMonitoring() {
    this.stopDurationMonitoring();

    this.durationConsumedSeconds = 0;
    this.emitDurationUpdate();

    this.durationDisplayIntervalId = setInterval(() => {
      if (!this.attempt_id) return;
      this.durationConsumedSeconds += 1;
      this.emitDurationUpdate();
    }, 1000);

    const intervalMs = this.DURATION_SYNC_SECONDS * 1000;
    this.durationIntervalId = setInterval(async () => {
      if (!this.attempt_id || this.durationSyncInFlight) return;

      this.durationSyncInFlight = true;
      try {
        const response = await apiClient.post("/client/attempt/duration", {});
        if (response?.success) {
          if (typeof response.duration === "number") {
            this.durationConsumedSeconds = Math.max(
              this.durationConsumedSeconds,
              Number(response.duration || 0),
            );
          } else {
            this.durationConsumedSeconds += this.DURATION_SYNC_SECONDS;
          }
          this.emitDurationUpdate();
        }
      } catch (error) {
        console.error("[Proctoring] Failed to sync duration:", error);
      } finally {
        this.durationSyncInFlight = false;
      }
    }, intervalMs);
  }

  static stopDurationMonitoring() {
    if (this.durationIntervalId) {
      clearInterval(this.durationIntervalId);
      this.durationIntervalId = null;
    }
    if (this.durationDisplayIntervalId) {
      clearInterval(this.durationDisplayIntervalId);
      this.durationDisplayIntervalId = null;
    }
    this.durationSyncInFlight = false;
  }

  static startPermissionWizard() {
    return new Promise((resolve, reject) => {
      UI.startPermissionWizard({
        user_details: {
          name: this.config.attempt.student_name,
          email: this.config.attempt.student_email,
          attempt_id: this.config.attempt.id,
          exam_name: this.config.attempt.exam_name,
        },
        theme: this.config.ui.theme,
        callbacks: {
          // Marked as async to allow the await apiClient.post call
          onComplete: async (streams) => {
            try {
              const response = await apiClient.post("/client/attempt/start", {
                id: this.config.attempt.id,
                user_id: this.config.attempt.user_id,
                session_id: this.config.attempt.session_id,
                institution_id: this.config.attempt.institution_id,
                student_name: this.config.attempt.student_name,
                student_email: this.config.attempt.student_email,
                exam_name: this.config.attempt.exam_name,
                mode: this.config.attempt.mode,
                session_duration: this.config.attempt.session_duration,
                allowed_chances: this.config.attempt.allowed_chances ?? 3,
              });

              if (!response.success) {
                throw new Error(
                  response.message || "Failed to start exam session",
                );
              }

              // Storing response data
              this.token = response.token;
              this.server_url = response.server_url;
              this.auth_token = response.auth_token;
              this.durationConsumedSeconds = Number(
                response.duration_consumed || 0,
              );
              this.emitDurationUpdate();

              if (this.auth_token) {
                localStorage.setItem("api_key", this.auth_token);
              }

              // Auto fullscreen if enabled
              if (this.config.ui.auto_fullscreen) {
                const elem = document.documentElement;
                if (elem.requestFullscreen) {
                  elem.requestFullscreen().catch(() => {});
                } else if (elem.webkitRequestFullscreen) {
                  elem.webkitRequestFullscreen().catch(() => {});
                } else if (elem.msRequestFullscreen) {
                  elem.msRequestFullscreen().catch(() => {});
                }
              }

              resolve(streams);
            } catch (error) {
              // Throw error to be caught by PermissionWizard for retry UI
              throw error;
            }
          },
          onCancel: () => {
            reject(new Error("Permission wizard cancelled by user"));
          },
        },
      });
    });
  }

  static async handleViolation(violation_type) {
    console.log(`Violation detected: ${violation_type}`);
    if (violation_type !== "periodic_snapshot") {
      UI.showViolationAlert(violation_type);
    }

    const currentMode =
      this.modes[this.config.attempt.mode] || this.modes.mode_full;

    // Capture evidence only if snapshots are enabled
    if (currentMode.snapshots) {
      const sources = ["camera"];
      if (this.screenStream) sources.push("screen");

      try {
        const images = await Snapshot.capture(sources, violation_type);

        // Upload images
        const formData = new FormData();
        formData.append("attemptId", this.config.attempt.id);
        formData.append("eventType", violation_type);
        formData.append("timestamp", new Date().toISOString());

        if (images.camera) {
          formData.append("camera", images.camera, "camera.jpg");
        }

        if (images.screen) {
          formData.append("screen", images.screen, "screen.jpg");
        }

        if (images.camera || images.screen) {
          await apiClient.post("/client/proctoring/image/upload", formData);
        }
      } catch (error) {
        console.error("[Proctoring] Failed to upload images:", error);
      }
    }

    // Send proctoring log to backend
    try {
      await apiClient.post("/client/proctoring/log", {
        eventType: violation_type,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("[Proctoring] Failed to send log:", error);
    }
  }

  static async stop(serverInitiated = false) {
    this.stopDurationMonitoring();

    Mediapipe.stop();
    Browser.stop();
    Livekit.stop();
    Hardware.stop();
    Snapshot.stop();

    if (this.attempt_id) {
      if (!serverInitiated) {
        try {
          await apiClient.get("/client/attempt/stop");
          console.log("[Proctoring] Exam stopped on server");
        } catch (error) {
          console.error("[Proctoring] Failed to stop exam on server:", error);
        }
      } else {
        console.log("[Proctoring] Exam already stopped by server");
      }
      this.attempt_id = null;
      this.durationConsumedSeconds = 0;
      localStorage.removeItem("api_key");
    }

    [this.cameraStream, this.screenStream].forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    });

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement.remove();
    }

    // Trigger onStop callback
    this.config.callbacks.onStop?.();

    UI.destroy();

    this.videoElement = null;
    this.cameraStream = null;
    this.screenStream = null;
  }
}

export default ProctoringSDK;
